// src/pages/onboarding/keywords.tsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import { useOnboarding } from "@/contexts/onboarding-context";
import { Link } from "react-router-dom";
import { getOnboardingRedirectPath } from "@/lib/onboarding-navigation-utils";

import { LoadingSpinner } from "@/components/ui/loading";
import { KeywordData, QualityData } from "@/types/metrics";
import { QualityScoreCategory } from "@/components/keyword-filter-controls";
import { DataDisplay } from "@/components/data-display";
import { TreeVisualization } from "@/components/tree-visualization";
import { KeywordTable } from "@/components/keyword-table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { formatQualityComponent } from "@/lib/quality-score-utils";
import { NgramTable } from "@/components/ngram-table";
import { processNgrams } from "@/lib/ngram-utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// Interface for processed keyword data
export interface ProcessedKeywordData extends KeywordData {
  score: number | null;
  scoreCategory: QualityScoreCategory;
  CTR: number;
  CPC: number;
  CvR: number;
  ROAS: number;
  CPA: number;
  matchType: string;
  expCTR?: string;
  adRelevance?: string;
  landingExp?: string;
}

type SortDirection = "asc" | "desc";

export default function OnboardingKeywordsPage() {
  const { data, loadingStatus, hasDataBeenFetched } = useOnboardingData();
  const { webAppUrl } = useOnboarding();
  const accountInfo = useMemo(
    () => (data?.account && data.account.length > 0 ? data.account[0] : null),
    [data],
  );
  const accountCurrency = accountInfo?.currency || "$";

  // states
  const [displayType, setDisplayType] = useState<"tree" | "table" | "ngram">(
    "tree",
  );
  const [wordCount, setWordCount] = useState(1);
  const [maxNameLength, setMaxNameLength] = useState(40);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    textFilters: {
      campaign: "",
      adGroup: "",
      keyword: "",
    },
    thresholds: {
      impr: 0,
      cost: 0,
      conv: 0,
    },
  });

  const [sliderMaxValues, setSliderMaxValues] = useState({
    impression: 1000,
    cost: 500,
  });
  const [isInitialFilterSet, setIsInitialFilterSet] = useState(false);

  const handleSliderChange = (
    value: number[],
    setter: (val: number) => void,
    max: number,
  ) => {
    const linearValue = value[0];
    if (max <= 1) {
      setter(Math.round((linearValue / 100) * max));
      return;
    }
    const logValue = Math.round(
      Math.pow(10, (linearValue / 100) * Math.log10(max)),
    );
    setter(logValue);
  };

  // Helper to get metric value at slider percentage (0-100)
  const sliderValueForPosition = (percentage: number, max: number) => {
    if (max <= 1) return Math.round((percentage / 100) * max);
    return Math.round(Math.pow(10, (percentage / 100) * Math.log10(max)));
  };

  const getSliderPosition = (value: number, max: number) => {
    if (value <= 0 || max <= 1) return 0;
    const position = (Math.log10(value) / Math.log10(max)) * 100;
    return Math.min(100, Math.max(0, position));
  };

  const collapseAll = useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // DEBUG: log first row of each data source when data is available
  useEffect(() => {
    if (!data) {
      console.log(
        "[Onboarding Data Check: keywords] Data is not yet available.",
      );
      return;
    }
    const logPrefix = "[Onboarding Data Check: keywords]";
    console.log(
      `${logPrefix} Using keyword80 data with ${data.keyword80?.length || 0} rows. Sample:`,
      data.keyword80?.slice(0, 2),
    );
    console.log(
      `${logPrefix} Using quality data with ${data.quality?.length || 0} rows. Sample:`,
      data.quality?.slice(0, 2),
    );
  }, [data]);

  // Join keyword and quality score data
  const processedData = useMemo(() => {
    if (!data) return [];

    const keywordData = data.keyword80 || [];
    const qualityData = data.quality || [];

    // Create index for quality data (handles variable column names)
    const qualityIndex = new Map<string, QualityData>();
    qualityData.forEach((q: any) => {
      const adGroupNorm = q.adGroup ?? q.adGroupName ?? "";
      const keywordNorm = q.keywordText ?? q.keyword ?? "";
      const matchTypeNorm = (
        q.matchType ??
        q.keywordMatchType ??
        ""
      ).toUpperCase();

      const key = `${q.campaign}|${adGroupNorm}|${keywordNorm}|${matchTypeNorm}`;
      qualityIndex.set(key, {
        ...q,
        adGroup: adGroupNorm, // ensure consistent property for downstream code
        keywordText: keywordNorm,
        matchType: matchTypeNorm,
        score: Number(q.qualityScore || q.score || 0),
      });
    });

    // Join and process data
    const processed = (keywordData as any[]).map((kw: any) => {
      const adGroupNorm = kw.adGroup ?? kw.adGroupName ?? "";
      const keywordNorm = kw.keywordText ?? kw.keyword ?? "";
      const matchTypeNorm = (
        kw.matchType ??
        kw.keywordMatchType ??
        ""
      ).toUpperCase();

      // Calculate derived metrics
      const CTR = kw.impr ? (kw.clicks / kw.impr) * 100 : 0;
      const CPC = kw.clicks ? kw.cost / kw.clicks : 0;
      const CvR = kw.clicks ? (kw.conv / kw.clicks) * 100 : 0;
      const ROAS = kw.cost ? kw.value / kw.cost : 0;
      const CPA = kw.conv ? kw.cost / kw.conv : 0;

      // Find matching quality data using normalised key
      const key = `${kw.campaign}|${adGroupNorm}|${keywordNorm}|${matchTypeNorm}`;
      const qualityInfo = qualityIndex.get(key);

      // Calculate score and score category
      const score = qualityInfo?.score ?? null;
      let scoreCategory: QualityScoreCategory = "NA";
      if (typeof score === "number") {
        if (score >= 8) scoreCategory = "great";
        else if (score >= 5) scoreCategory = "ok";
        else scoreCategory = "poor";
      }

      // Format quality components
      const expCTR = formatQualityComponent(qualityInfo?.expCTR);
      const adRelevance = formatQualityComponent(qualityInfo?.adRelevance);
      const landingExp = formatQualityComponent(qualityInfo?.landingExp);

      return {
        ...kw,
        adGroup: adGroupNorm, // ensure property exists for UI & filters
        keywordText: keywordNorm, // ensure property exists for UI & filters
        score,
        scoreCategory,
        matchType: matchTypeNorm,
        expCTR,
        adRelevance,
        landingExp,
        CTR,
        CPC,
        CvR,
        ROAS,
        CPA,
      } as ProcessedKeywordData;
    });

    if (process.env.NODE_ENV === "development") {
      console.log("Processed keyword data stats:", {
        processedCount: processed.length,
        withQualityScore: processed.filter((p) => p.score !== null).length,
        processedSample: processed.slice(0, 2),
      });
    }

    return processed;
  }, [data]);

  // Effect to set slider ranges and initial values once data is processed
  useEffect(() => {
    if (processedData.length > 0 && !isInitialFilterSet) {
      const maxImpressions = Math.max(...processedData.map((k) => k.impr), 0);
      const maxCost = Math.max(...processedData.map((k) => k.cost), 0);

      setSliderMaxValues({
        impression: maxImpressions > 0 ? maxImpressions : 1000,
        cost: maxCost > 0 ? maxCost : 500,
      });

      setFilters((prev) => ({
        ...prev,
        thresholds: {
          ...prev.thresholds,
          impr: sliderValueForPosition(
            10,
            maxImpressions > 0 ? maxImpressions : 1000,
          ),
          cost: sliderValueForPosition(10, maxCost > 0 ? maxCost : 500),
          conv: 0,
        },
      }));
      setIsInitialFilterSet(true);
    }
  }, [processedData, isInitialFilterSet]);

  const [sort, setSort] = useState<{ field: string; direction: SortDirection }>(
    {
      field: displayType === "ngram" ? "impr" : "cost",
      direction: "desc",
    },
  );
  const [visibleColumns, setVisibleColumns] = useState(
    new Set([
      "campaign",
      "adGroup",
      "keywordText",
      "keywordMatchType",
      "score",
      "impr",
      "clicks",
      "CTR",
      "cost",
      "CPC",
      "conv",
      "value",
      "ROAS",
      "CPA",
    ]),
  );
  const [ngramVisibleColumns, setNgramVisibleColumns] = useState(
    new Set([
      "ngram",
      "count",
      "impr",
      "clicks",
      "CTR",
      "cost",
      "CPC",
      "conv",
      "CvR",
      "value",
      "ROAS",
    ]),
  );
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const MAX_TREE_NODES = 1000;

  // Filter and process data
  const filteredData = useMemo(() => {
    const sourceData = processedData || [];

    if (!sourceData?.length) return [];

    const filtered = sourceData.filter((row) => {
      // Apply text filters for keywords
      if (
        filters.textFilters.campaign &&
        !row.campaign
          .toLowerCase()
          .includes(filters.textFilters.campaign.toLowerCase())
      )
        return false;
      if (
        filters.textFilters.adGroup &&
        !row.adGroup
          .toLowerCase()
          .includes(filters.textFilters.adGroup.toLowerCase())
      )
        return false;
      if (
        filters.textFilters.keyword &&
        !row.keywordText
          .toLowerCase()
          .includes(filters.textFilters.keyword.toLowerCase())
      )
        return false;

      // Apply threshold filters for keywords
      if (row.impr < filters.thresholds.impr) return false;
      if (row.cost < filters.thresholds.cost) return false;
      if (row.conv < filters.thresholds.conv) return false;

      return true;
    });

    return filtered;
  }, [processedData, filters]);

  // Process ngrams
  const ngramData = useMemo(() => {
    let processedNgrams = processNgrams(
      filteredData as ProcessedKeywordData[],
      3,
      wordCount,
    );

    // Sort ngrams
    const sorted = [...processedNgrams].sort((a, b) => {
      const aValue = a[sort.field as keyof typeof a];
      const bValue = b[sort.field as keyof typeof b];
      const multiplier = sort.direction === "asc" ? 1 : -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return multiplier * aValue.localeCompare(bValue);
      }
      return multiplier * ((Number(aValue) || 0) - (Number(bValue) || 0));
    });

    return sorted;
  }, [filteredData, sort, wordCount]);

  // For ngram view, we need to apply metric filters to the ngram data itself
  const filteredNgramData = useMemo(() => {
    if (displayType !== "ngram") return ngramData;

    return ngramData.filter((ngram) => {
      if (ngram.impr < filters.thresholds.impr) return false;
      if (ngram.cost < filters.thresholds.cost) return false;
      if (ngram.conv < filters.thresholds.conv) return false;

      return true;
    });
  }, [ngramData, displayType, filters]);

  // Update sort when switching to ngram view
  useEffect(() => {
    if (displayType === "ngram") {
      setSort({ field: "impr", direction: "desc" });
    }
  }, [displayType]);

  // Sort data (for table and tree views)
  const sortedData = useMemo(() => {
    if (displayType === "ngram") return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sort.field as keyof typeof a];
      const bValue = b[sort.field as keyof typeof b];
      const multiplier = sort.direction === "asc" ? 1 : -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return multiplier * aValue.localeCompare(bValue);
      }
      return multiplier * ((Number(aValue) || 0) - (Number(bValue) || 0));
    });
  }, [filteredData, sort, displayType]);

  // Ensure compatibility with DataDisplay (which includes 'insights')
  const handleViewTypeChange = (
    type: "tree" | "table" | "ngram" | "insights",
  ) => {
    if (type === "insights") return; // ignore unsupported view
    setDisplayType(type);
  };

  // By the time we get here, data should be loaded. If not, show a message.
  if (!hasDataBeenFetched || !data || !processedData?.length) {
    const redirect = getOnboardingRedirectPath(webAppUrl, !!data);

    return (
      <main className="flex-1 flex items-center justify-center p-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Data Available</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>{redirect.message}</p>
            <Button asChild>
              <Link to={redirect.path}>Continue</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <main className="flex-1 p-4 lg:p-6 space-y-4">
      {/* Filter Section */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Text Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="campaign-filter">Campaign</Label>
                <Input
                  id="campaign-filter"
                  placeholder="Filter campaigns..."
                  value={filters.textFilters.campaign}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      textFilters: {
                        ...prev.textFilters,
                        campaign: e.target.value,
                      },
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adgroup-filter">Ad Group</Label>
                <Input
                  id="adgroup-filter"
                  placeholder="Filter ad groups..."
                  value={filters.textFilters.adGroup}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      textFilters: {
                        ...prev.textFilters,
                        adGroup: e.target.value,
                      },
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="keyword-filter">Keyword</Label>
                <Input
                  id="keyword-filter"
                  placeholder="Filter keywords..."
                  value={filters.textFilters.keyword}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      textFilters: {
                        ...prev.textFilters,
                        keyword: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            </div>

            {/* Threshold Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Label>
                  Minimum Impressions:{" "}
                  {filters.thresholds.impr.toLocaleString()}
                </Label>
                <Slider
                  value={[
                    getSliderPosition(
                      filters.thresholds.impr,
                      sliderMaxValues.impression,
                    ),
                  ]}
                  onValueChange={(value) =>
                    handleSliderChange(
                      value,
                      (val) =>
                        setFilters((prev) => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, impr: val },
                        })),
                      sliderMaxValues.impression,
                    )
                  }
                  max={100}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0</span>
                  <span>{sliderMaxValues.impression.toLocaleString()}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Label>
                  Minimum Cost: {accountCurrency}
                  {filters.thresholds.cost.toLocaleString()}
                </Label>
                <Slider
                  value={[
                    getSliderPosition(
                      filters.thresholds.cost,
                      sliderMaxValues.cost,
                    ),
                  ]}
                  onValueChange={(value) =>
                    handleSliderChange(
                      value,
                      (val) =>
                        setFilters((prev) => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, cost: val },
                        })),
                      sliderMaxValues.cost,
                    )
                  }
                  max={100}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{accountCurrency}0</span>
                  <span>
                    {accountCurrency}
                    {sliderMaxValues.cost.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Minimum Conversions: {filters.thresholds.conv}</Label>
                <Slider
                  value={[filters.thresholds.conv]}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      thresholds: { ...prev.thresholds, conv: value[0] },
                    }))
                  }
                  max={50}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0</span>
                  <span>50+</span>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                {displayType === "ngram"
                  ? filteredNgramData.length
                  : sortedData.length}{" "}
                of {processedData.length} keywords
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilters({
                    textFilters: { campaign: "", adGroup: "", keyword: "" },
                    thresholds: { impr: 0, cost: 0, conv: 0 },
                  });
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <DataDisplay
        viewType={displayType}
        onViewTypeChange={handleViewTypeChange}
        pageContextType="keywords"
        data={displayType === "ngram" ? filteredNgramData : sortedData}
        treeComponent={(props) => (
          <>
            {sortedData.length > MAX_TREE_NODES && (
              <Alert className="mb-4">
                <AlertDescription>
                  Showing top {MAX_TREE_NODES} of {sortedData.length} matching
                  items. Please adjust filters to see other results.
                </AlertDescription>
              </Alert>
            )}
            <div className="text-lg">
              <TreeVisualization
                data={sortedData.slice(0, MAX_TREE_NODES)}
                type={"keywords"}
                maxLength={maxNameLength}
                onMaxLengthChange={setMaxNameLength}
                {...props}
              />
            </div>
          </>
        )}
        tableComponent={
          <KeywordTable
            data={sortedData as ProcessedKeywordData[]}
            visibleColumns={visibleColumns}
            sort={sort}
            onSortChange={(field) => {
              setSort((prev) => ({
                field,
                direction:
                  prev.field === field
                    ? prev.direction === "asc"
                      ? "desc"
                      : "asc"
                    : "desc",
              }));
            }}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            accountCurrency={accountCurrency}
          />
        }
        ngramComponent={
          <NgramTable
            data={filteredNgramData}
            visibleColumns={ngramVisibleColumns}
            sort={sort}
            onSortChange={(field) => {
              setSort((prev) => ({
                field,
                direction:
                  prev.field === field
                    ? prev.direction === "asc"
                      ? "desc"
                      : "asc"
                    : "desc",
              }));
            }}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            accountCurrency={accountCurrency}
            type={"keywords"}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
          />
        }
        hideShowKeywords
        onColumnsChange={
          displayType === "ngram" ? setNgramVisibleColumns : setVisibleColumns
        }
        visibleColumns={
          displayType === "ngram" ? ngramVisibleColumns : visibleColumns
        }
        onRowsPerPageChange={setRowsPerPage}
        rowsPerPage={rowsPerPage}
        type={"keywords"}
        wordCount={wordCount}
        onWordCountChange={setWordCount}
        hasExpandedRows={expandedRows?.size > 0}
        onCollapseAll={collapseAll}
        maxLength={maxNameLength}
        onMaxLengthChange={setMaxNameLength}
      />
      {/* Navigation to next step */}
      <div className="text-center pt-8 mt-8 border-t">
        <Link to="/onboarding/search-terms">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3">
            Next: Explore the Search Terms Tool
          </Button>
        </Link>
      </div>
    </main>
  );
}
